/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, Cell, Grid, Heading, Text } from 'bold-ui'
import CheckPermission from 'components/auth/CheckPermission'
import { AlergiaSelectFieldModel } from 'components/form/field/select/AlergiaSelectField/AlergiaSelectField'
import { useCallback } from 'react'
import { Alergia } from 'view/atendimentos/detail/soap/aside/types/AlergiaModel'
import { grupoCboAlergias } from 'view/atendimentos/detail/soap/avaliacao/acessos'
import { AlergiaReacaoModel } from 'view/atendimentos/detail/soap/avaliacao/components/alergias-reacoes/model'

import { useEditableListField } from '../../EditableList'
import { AccordionAlergiasModal } from './AccordionAlergiasModal'
import { CadastroAlergiasModal } from './CadastroAlergiasModal'
import { EditableListaAlergiasModalProps } from './ListaAlergiasModal'

const MENSAGEM_NENHUMA_ALERGIA = 'Nenhuma alergia ou reação adversa registrada.'

export interface EditableListaAlergiasModalBodyProps extends EditableListaAlergiasModalProps {
  todasAlergias: Alergia[]
  alergiasCidadao: AlergiaSelectFieldModel[]
}

export function EditableListaAlergiasModalBody({
  fieldName,
  alergiasAtendimentoAtual,
  todasAlergias,
  alergiasCidadao,
  ...cadastroModalProps
}: EditableListaAlergiasModalBodyProps) {
  const {
    handleSubmit,
    input: { value },
    removeItem,
    handleRowChanged,
  } = useEditableListField({
    name: fieldName,
  })

  const findFormModel = useCallback(
    (alergiaId: ID) =>
      alergiasAtendimentoAtual.find(
        (alergia: AlergiaReacaoModel) => alergia.alergiaId === alergiaId || alergia._id === alergiaId
      ),
    [alergiasAtendimentoAtual]
  )

  return (
    <Grid>
      <Cell size={12}>
        <Heading level={1}>Alergias/Reações adversas</Heading>
      </Cell>
      <Cell size={12}>
        <Alert inline type='info'>
          As alergias e reações adversas que precisem de diagnóstico/evolução devem ser inseridas pela avaliação. Os
          registros feitos nesta seção não evoluirão nenhuma alergia ou reação adversa.
        </Alert>
      </Cell>
      <Cell size={12}>
        <CheckPermission permission={grupoCboAlergias.adicionarEditarExcluir}>
          <CadastroAlergiasModal
            onSubmit={handleSubmit}
            formValues={value}
            alergiasCidadao={alergiasCidadao}
            {...cadastroModalProps}
          />
        </CheckPermission>
      </Cell>
      <Cell size={12}>
        {todasAlergias.length === 0 ? (
          <Text component='p'>{MENSAGEM_NENHUMA_ALERGIA}</Text>
        ) : (
          <AccordionAlergiasModal
            alergias={todasAlergias}
            showAlteracaoExclusao
            onRemove={removeItem}
            onEdit={handleRowChanged}
            findFormModel={findFormModel}
          />
        )}
      </Cell>
    </Grid>
  )
}

export interface ReadOnlyListaAlergiasModalBodyProps {
  todasAlergias: Alergia[]
}

export function ReadOnlyListaAlergiasModalBody({ todasAlergias }: ReadOnlyListaAlergiasModalBodyProps) {
  return (
    <Grid>
      <Cell size={12}>
        <Heading
          level={1}
          style={css`
            margin-bottom: 1rem;
          `}
        >
          Alergias/Reações adversas
        </Heading>
      </Cell>
      <Cell size={12}>
        {todasAlergias.length === 0 ? (
          <Text component='p'>{MENSAGEM_NENHUMA_ALERGIA}</Text>
        ) : (
          <AccordionAlergiasModal alergias={todasAlergias} showAlteracaoExclusao={false} />
        )}
      </Cell>
    </Grid>
  )
}
